/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Card, Grid, Typography } from '@mui/material';
import React from 'react';
import { LastUpdateTime, useConfirmationDialog } from '@opengeoweb/shared';
import { LocationTabs } from './LocationTabs';
import { TacOverview, TacOverviewMobile } from './TacOverview';
import { TafPanel } from './TafPanel';
import { TopTabs } from './TopTabs';
import type { TafError } from './utils';
import {
  getNewTafLocationIndex,
  getPreviousTaf,
  sortTafTimeSlots,
} from './utils';
import type { TafFromBackend, TafFromFrontEnd, TimeSlot } from '../../types';
import { useTafTranslation } from '../../utils/i18n';
import { AutoTaf } from '../AutoTaf';
import { MODAL_DIALOG_ELEMENT, useIssuesPane } from '../IssuesPane/IssuesPane';
import IssuesPaneWrapper from '../IssuesPane/IssuesPaneWrapper';
import { fromActionToStatus } from './TafPanel/TafPanel';
import { usePatchTaf, usePostTaf } from '../../utils/taf-api/hooks';
import { tafActionToDraftAction } from '../TafForm/TafFormButtons/TafFormButtons';
import { useTafModuleContext } from '../TafModule/TafModuleProvider';
import { useTafConfigContext } from '../TafWrapper/TafConfigWrapper';

const filterAutoTafs = (
  tafList: TafFromBackend[],
): {
  tafList: TafFromBackend[];
  autoTafList: TafFromBackend[];
} =>
  tafList.reduce(
    (tList, tafFromBackend) => {
      const { status } = tafFromBackend.taf;
      const listToAdd = status === 'AUTO' ? 'autoTafList' : 'tafList';

      return {
        ...tList,
        [listToAdd]: tList[listToAdd].concat(tafFromBackend),
      };
    },
    {
      tafList: [] as TafFromBackend[],
      autoTafList: [] as TafFromBackend[],
    },
  );

interface TafLayoutProps {
  tafList: TafFromBackend[];
  onUpdateTaf: () => Promise<void>;
  lastUpdateTime?: string;
  isAutoTafEnabled?: boolean;
}

const TafLayout: React.FC<TafLayoutProps> = ({
  tafList: completeTafList,
  onUpdateTaf = async (): Promise<void> => null!,
  lastUpdateTime,
  isAutoTafEnabled = false,
}: TafLayoutProps) => {
  const { tafList, autoTafList } = React.useMemo(
    () => filterAutoTafs(completeTafList),
    [completeTafList],
  );

  const { t } = useTafTranslation();
  const confirmDialog = useConfirmationDialog();
  const {
    isIssuesPaneOpen,
    onToggleIssuesPane,
    onCloseIssuesPane,
    startPosition,
    onUserHasDragged,
  } = useIssuesPane();
  const { isPending: isPostTafPending, mutateAsync: postTafRequest } =
    usePostTaf();
  const { mutateAsync: patchTafRequest } = usePatchTaf();

  const { onValidateForm, tafAction } = useTafModuleContext();

  const { tafConfig } = useTafConfigContext();

  const [{ upcoming, current, expired }, updateSortedTafs] = React.useState(
    sortTafTimeSlots(tafList, tafConfig!),
  );

  const lastCurrentTaf: TafFromBackend | undefined =
    current.length > 0 ? current[current.length - 1] : undefined;

  const [timeSlot, setTimeSlot] = React.useState<TimeSlot>('UPCOMING');
  const [locations, setLocations] = React.useState<TafFromBackend[]>(upcoming);
  const [activeTafIndex, setActiveTafIndex] = React.useState<number>(0);
  const [isFormDisabled, setIsFormDisabled] = React.useState<boolean>(false);
  const [error, setError] = React.useState<TafError | null>(null);

  const postTaf = async (
    data: TafFromFrontEnd,
    isAutoSaved = false,
  ): Promise<void> => {
    await postTafRequest({ tafData: data, isAutoSaved, setError });
  };

  const updateEditor = async (
    data: TafFromFrontEnd,
    force: boolean,
  ): Promise<Response | null> =>
    patchTafRequest(
      { tafData: data, setError, force },
      {
        onError: (e: Error) => {
          if (e.cause === 409) {
            void confirmDialog({
              title: t('switch-title'),
              description: t('switch-description'),
              confirmLabel: t('switch-confirm'),
            }).then(() => void updateEditor(data, true));
          }
        },
      },
    );

  const patchTaf = async (data: TafFromFrontEnd): Promise<boolean> => {
    try {
      await updateEditor(data, false);
      return true;
    } catch {
      return false;
    }
  };

  const CONFIRM_DIALOG_OPTIONS = {
    title: t('confirm-dialog-title'),
    description: t('confirm-dialog-description'),
    confirmLabel: t('confirm-dialog-confirm-label'),
  };

  async function onChangeTab<T>(
    action: (param: T) => void,
    value: T,
    isFormDirty: boolean,
  ): Promise<void> {
    if (isFormDirty) {
      try {
        const taf = await onValidateForm!();
        await postTaf(
          {
            taf,
            changeStatusTo: fromActionToStatus(
              tafActionToDraftAction(tafAction!),
            ),
          } as TafFromFrontEnd,
          true,
        );
        action(value);
      } catch {
        void confirmDialog(CONFIRM_DIALOG_OPTIONS).then(() => {
          action(value);
          setError(null);
        });
      }
    } else {
      action(value);
      setError(null);
    }
  }

  const onChangeLocationTab = (index: number, isFormDirty: boolean): void => {
    void onChangeTab(setActiveTafIndex, index, isFormDirty);
  };

  const onChangeTimeSlot = (
    newTimeSlot: TimeSlot,
    isFormDirty: boolean,
  ): void => {
    void onChangeTab(changeTimeSlot, newTimeSlot, isFormDirty);
  };

  const changeTimeSlot = (newTimeSlot: TimeSlot): void => {
    setTimeSlot(newTimeSlot);
    setLocations(newTimeSlot === 'ACTIVE' ? current : upcoming);
    setActiveTafIndex(0);
  };

  React.useEffect(() => {
    const newSortedTafs = sortTafTimeSlots(tafList, tafConfig!);
    const updatedLocations =
      timeSlot === 'ACTIVE' ? newSortedTafs.current : newSortedTafs.upcoming;
    updateSortedTafs(sortTafTimeSlots(tafList, tafConfig!));
    setLocations(updatedLocations);

    // update active index
    const previousActiveLocationUUID = locations[activeTafIndex]?.taf?.uuid;
    const newTafLocationIndex = getNewTafLocationIndex(
      updatedLocations,
      previousActiveLocationUUID,
    );
    setActiveTafIndex(newTafLocationIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tafList, setLocations]);

  React.useEffect(() => {
    const cancelErrorTimeout =
      error?.type === 'CANCELLED'
        ? setTimeout(() => {
            setError(null);
          }, 10000)
        : null;
    return (): void => {
      if (cancelErrorTimeout !== null) {
        clearTimeout(cancelErrorTimeout);
      }
    };
  }, [error]);

  if (!tafList?.length) {
    return null;
  }
  const activeTaf = locations[activeTafIndex];

  return (
    <>
      <Box
        component="div"
        id={MODAL_DIALOG_ELEMENT}
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      />
      <Box sx={{ height: '100%', maxHeight: '100%' }}>
        <Box
          sx={{
            minWidth: 320,
            margin: 'auto',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Grid container spacing={2} sx={{ paddingLeft: 1, paddingRight: 1 }}>
            <Grid size={{ xs: 12, sm: 9 }}>
              <TopTabs
                onChangeTimeSlot={onChangeTimeSlot}
                tafFromBackend={lastCurrentTaf}
                timeSlot={timeSlot}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              {lastUpdateTime ? (
                <LastUpdateTime
                  dataTestId="lastupdated-time"
                  lastUpdateTime={lastUpdateTime}
                  onPressRefresh={onUpdateTaf}
                />
              ) : null}
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            sx={{
              height: '100%',
              paddingLeft: 1,
              paddingRight: 1,
              paddingTop: 2,
              overflow: 'auto',
            }}
          >
            <Grid size={{ xs: 3, sm: 1 }}>
              <LocationTabs
                activeIndex={activeTafIndex}
                onChangeTab={onChangeLocationTab}
                tafList={locations}
                timeSlot={timeSlot}
              />
            </Grid>
            <Grid size="grow">
              <Card
                sx={{
                  marginBottom: '12px',
                  position: 'relative',
                  minHeight: locations.length * 62 + 32,
                }}
                variant="outlined"
              >
                <TacOverviewMobile
                  activeTaf={activeTaf}
                  currentTafs={current}
                  expiredTafs={expired}
                  upcomingTafs={upcoming}
                  {...(isAutoTafEnabled && {
                    autoTafs: autoTafList,
                  })}
                />
                {locations.length > 0 ? (
                  locations.map((tafFromBackend, index) => (
                    <TafPanel
                      activeTafIndex={activeTafIndex}
                      error={error}
                      index={index}
                      isFormDisabled={isFormDisabled}
                      isIssuesPaneOpen={isIssuesPaneOpen}
                      isLoading={isPostTafPending}
                      key={tafFromBackend.taf.uuid}
                      patchTaf={patchTaf}
                      postTaf={postTaf}
                      previousTaf={getPreviousTaf(tafList, tafFromBackend)}
                      setIsFormDisabled={setIsFormDisabled}
                      setIsIssuesPaneOpen={onToggleIssuesPane}
                      shouldEnableRemark={tafConfig?.shouldEnableRemark}
                      tafAction={tafAction!}
                      tafFromBackend={tafFromBackend}
                    />
                  ))
                ) : (
                  <Typography sx={{ margin: 1 }} variant="body1">
                    {t('no-locations-message')}
                  </Typography>
                )}
              </Card>
            </Grid>
            <Grid
              size={{
                xs: 0,
                sm: 3,
              }}
              sx={{
                height: { xs: 0, sm: '100%' },
                ...(isAutoTafEnabled && {
                  display: 'flex',
                  flexDirection: 'column',
                }),
              }}
            >
              <TacOverview
                activeTaf={activeTaf}
                currentTafs={current}
                expiredTafs={expired}
                isFormDisabled={isFormDisabled}
                upcomingTafs={upcoming}
              />
              {isAutoTafEnabled ? (
                <AutoTaf
                  activeTaf={activeTaf}
                  isFormDisabled={isFormDisabled}
                  tafList={autoTafList}
                />
              ) : null}
            </Grid>
          </Grid>
        </Box>
      </Box>
      <IssuesPaneWrapper
        handleClose={onCloseIssuesPane}
        isOpen={isIssuesPaneOpen}
        onUserHasDragged={onUserHasDragged}
        startPosition={startPosition}
      />
    </>
  );
};

export default TafLayout;
